/*
  Core configuration files
 */
import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {ProxyConfiguration} from "./proxy-configuration";
import {SearchConfiguration} from "./search-configuration";
import {WebMapConfiguration} from "./web-map-configuration";
import {MapViewConfiguration} from "./view-configuration";
import {BasemapGalleryConfiguration} from "./basemap-gallery-configuration";
import {BasemapToggleConfiguration} from "./basemap-toggle-configuration";
import {AttributionConfiguration} from "./attribution-configuration";
import {MaintenanceConfiguration} from "./maintenance-configuration";
import {ScaleBarConfiguration} from "./scalebar-configuration";
import {LegendConfiguration} from "./legend-configuration";
import {ExternalServiceConfiguration} from "./external-service-configuration";
import {ActionButtonConfiguration} from "./action-configuration";
import {SketchConfiguration} from "./sketch-configuration";
import {LayerConfiguration} from "./layer-configuration";
import {EditorConfiguration} from "./editor-configuration";

@JsonObject()
export class AppConfiguration {

    /**
     * Attribution (copyright, legal notice)
     */
    @JsonProperty()
    declare attribution: AttributionConfiguration;

    /**
     * Basisname der Anwendung
     */
    @JsonProperty()
    declare appBasename: string;

    /**
     * Basisname der Anwendung in den meisten Fällen identisch mit appBasename
     */
    @JsonProperty()
    declare basename: string;

    /**
     * The base url for configuration files
     */
    @JsonProperty()
    declare configRoot: string;

    /**
     * application mode (development | test | production)
     */
    @JsonProperty()
    declare mode: string;

    /**
     * Information about maintenance work on the application
     */
    @JsonProperty({ type: MaintenanceConfiguration})
    declare maintenance: MaintenanceConfiguration[];

    /**
     * Proxy URL
     */
    @JsonProperty({ type: ProxyConfiguration })
    declare proxy: ProxyConfiguration;

    /**
     * URL des Portal for ArcGIS oder AOL
     */
    @JsonProperty()
    declare portalUrl: string;

    /**
     * App ID.
     * ID you get when registering the App on Portal for ArcGIS
     * Needed when using OAuth2 with ESRI IdentityManager
     */
    @JsonProperty()
    declare appId?: string;

    /**
     * Overrides the URL for loading the API assets when using local builds of the `@arcgis/core` and `arcgis-js-api` NPM packages.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#assetsPath)
     */
    @JsonProperty()
    declare assetsPath: string;
    /**
     * The URL for font resources used by the Font class in FeatureLayer and CSVLayer labels. To use your own hosted fonts, the font files need to be in .pbf format, and you must follow the kebab-case naming convention (e.g. "arial-unicode-ms-bold").
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#fontsUrl)
     */
    @JsonProperty()
    declare fontsUrl: string;
    /**
     * The default [GeometryService](https://developers.arcgis.com/javascript/latest/api-reference/esri-tasks-GeometryService.html) used by widgets and other operations, such as on-the-fly projections.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#geometryServiceUrl)
     */
    @JsonProperty()
    declare geometryServiceUrl: string;

    /**
     * WebMap
     */
    @JsonProperty()
    declare webMap: WebMapConfiguration;

    /**
     * View settings
     */
    @JsonProperty()
    declare mapView: MapViewConfiguration;

    /**
     * next base map
     */
    @JsonProperty()
    declare nextBasemap: string;

    /**
     * search widget
     */
    @JsonProperty({ type: SearchConfiguration})
    declare search: SearchConfiguration;

    /**
     * basemap gallery widget
     */
    @JsonProperty({ type: BasemapGalleryConfiguration})
    declare basemapGallery: BasemapGalleryConfiguration;

    @JsonProperty()
    declare basemapToggle: BasemapToggleConfiguration;

    @JsonProperty()
    declare elevationLayers?: LayerConfiguration[];

    @JsonProperty()
    declare legend: LegendConfiguration;

    @JsonProperty()
    declare scaleBar: ScaleBarConfiguration;

    @JsonProperty()
    declare editor: EditorConfiguration;

    @JsonProperty()
    declare sketch: SketchConfiguration;

    @JsonProperty()
    declare externalServices: ExternalServiceConfiguration[];

    @JsonProperty()
    declare predefinedActions: ActionButtonConfiguration[];

    constructor() {}
}
